/**
 * /privacy-choices — "Do Not Sell or Share My Personal Information".
 *
 * Public: anyone can opt out on their browser; a signed-in customer's choice
 * is also saved to their account, so their bookings stop being reported to
 * ad measurement (server/privacy-opt-outs.js). A browser that sends Global
 * Privacy Control is opted out automatically and can't be switched back
 * here. Everything the page promises is in the concierge's knowledge base
 * ("Account Security & Privacy").
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import HelpShell from '@/components/help/HelpShell';
import { Switch } from '@/components/ui/switch';
import { gpcEnabled, hasOptedOutHere, setOptedOutHere, markSynced } from '@/lib/privacyChoices';
import { postPrivacyChoice } from '@/hooks/usePrivacyChoiceSync';
import PageSeo from '@/components/PageSeo';

const PANEL = 'rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.015]';
const LINK = 'text-[#AAB4D6] underline decoration-white/25 underline-offset-2 hover:text-white';

export default function PrivacyChoices() {
  const gpc = gpcEnabled();
  const [optedOut, setOptedOut] = useState(() => gpc || hasOptedOutHere());
  const [session, setSession] = useState(null);
  const [accountOptedOut, setAccountOptedOut] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(async ({ data }) => {
      const s = data?.session || null;
      if (!alive) return;
      setSession(s);
      if (!s?.access_token) return;
      try {
        const res = await fetch('/api/privacy/opt-out', { headers: { Authorization: `Bearer ${s.access_token}` } });
        const body = res.ok ? await res.json() : null;
        if (!alive || !body) return;
        setAccountOptedOut(Boolean(body.opted_out));
        if (body.opted_out) setOptedOut(true);
      } catch {
        /* the browser choice still works */
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const choose = async (next) => {
    if (gpc && !next) return;
    setStatus('saving');
    setOptedOutHere(next);
    setOptedOut(next || gpc);
    let ok = true;
    if (session?.access_token) {
      try {
        ok = await postPrivacyChoice(session.access_token, { optedOut: next, source: 'page' });
        if (ok) {
          markSynced(session.user?.id, next);
          setAccountOptedOut(next);
        }
      } catch {
        ok = false;
      }
    }
    setStatus(ok ? 'saved' : 'error');
    // Tags already running on this page keep running until it reloads.
    if (ok && next) window.setTimeout(() => window.location.reload(), 900);
  };

  return (
    <HelpShell showSearch={false} homeLabel="Privacy choices" homeHref="/privacy-choices">
      <PageSeo path="/privacy-choices" />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 pt-14 sm:pt-20 pb-24">
        <p className="text-[12px] uppercase tracking-[0.24em] text-[#AAB4D6]">Privacy</p>
        <h1 className="mt-3 text-[34px] sm:text-[44px] leading-[1.1] font-semibold tracking-[-0.02em] text-white">Your privacy choices</h1>
        <p className="mt-5 text-[16px] leading-relaxed text-white/70">
          We use advertising and analytics partners to measure our ads and to understand how our site is used. Some privacy laws treat sharing information with advertising partners as a “sale” or “sharing” of personal information. You can opt out here at any time.
        </p>

        <section aria-labelledby="choice" className={`${PANEL} mt-9 px-6 py-6 sm:px-7`}>
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0">
              <h2 id="choice" className="text-[18px] font-medium text-white">Do not sell or share my personal information</h2>
              <p className="mt-1.5 text-[14.5px] leading-relaxed text-white/65" aria-live="polite">
                {optedOut ? 'You’re opted out on this browser.' : 'You’re not opted out.'}
                {status === 'saving' && ' Saving…'}
                {status === 'saved' && ' Saved.'}
                {status === 'error' && ' Your choice is saved on this browser, but we couldn’t save it to your account. Please try again.'}
              </p>
            </div>
            <Switch
              checked={optedOut}
              disabled={gpc || status === 'saving'}
              onCheckedChange={choose}
              aria-labelledby="choice"
              className="mt-1 data-[state=checked]:bg-[#7A8ABF] data-[state=unchecked]:bg-white/20"
            />
          </div>

          {gpc && (
            <p className="mt-4 flex items-start gap-2.5 rounded-xl bg-[#7A8ABF]/10 px-4 py-3 text-[14px] leading-relaxed text-white/80">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#C9D0EA]" strokeWidth={1.5} aria-hidden />
              Your browser is sending a Global Privacy Control signal, so you’re opted out on this browser automatically.
            </p>
          )}

          <p className="mt-4 text-[14px] leading-relaxed text-white/60">
            {session
              ? (accountOptedOut
                ? 'Your choice is also saved to your Defera account, so we don’t send your bookings to our advertising partners.'
                : 'Because you’re signed in, opting out also saves the choice to your Defera account, so we stop sending your bookings to our advertising partners.')
              : 'This choice applies to this browser. If you have a Defera account, sign in on this browser and come back here, so it applies to your bookings too.'}
          </p>
        </section>

        <section aria-labelledby="what-it-does" className="mt-10">
          <h2 id="what-it-does" className="text-[20px] font-medium text-white">What opting out does</h2>
          <ul className="mt-4 list-disc space-y-2.5 pl-5 text-[15.5px] leading-relaxed text-white/75 marker:text-white/35">
            <li>We stop loading our advertising measurement tags and our site analytics tag on this browser.</li>
            <li>If you’re signed in, we stop sending your bookings to our advertising partners to measure our ads.</li>
            <li>Nothing changes about your trips, your payments or the emails we send you about them.</li>
          </ul>
        </section>

        <section aria-labelledby="gpc" className="mt-10">
          <h2 id="gpc" className="text-[20px] font-medium text-white">Global Privacy Control</h2>
          <p className="mt-3 text-[15.5px] leading-relaxed text-white/75">
            We treat a Global Privacy Control signal from your browser as a request to opt out, so you don’t need to do anything else here.
          </p>
        </section>

        <p className="mt-12 text-[14.5px] leading-relaxed text-white/60">
          Our <Link to="/privacy" className={LINK}>Privacy Policy</Link> explains what we collect and your rights over it, and our{' '}
          <Link to="/security/data-protection" className={LINK}>Security Center</Link> explains how we protect it.
        </p>
      </div>
    </HelpShell>
  );
}
